import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react"
import { TextAnimateGradient } from "../common/text-animate-gradient";
import { ThinkingData } from "@/services/push-info-origin-data-record-list";
import { useEffect, useRef, useState } from "react"
import { useTypewriter } from "@/hooks/use-typewriter";
import { DotIcon } from "./assets/dot-icon";
import { CancelTaskIcon } from "./assets/cancel-task-icon";

interface ThinkingProcessProps {
  /** 标题 */
  title: string;
  /** 思考列表 */
  thinkingList?: ThinkingData[];
  /** 是否开始打字 */
  startTyping?: boolean;
  /** 是否启用展开 */
  enabledExpand?: boolean;
  /** 是否展开 */
  expand?: boolean;
  /** 是否正在思考 */
  isThinking?: boolean;
  /** 是否展示取消按钮 */
  showCancel?: boolean;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  onCancel?: () => void;
}

/** 思考过程 */
export const ThinkingProcess = ({
  title,
  thinkingList = [],
  className,
  headerClassName,
  titleClassName,
  startTyping,
  isThinking,
  enabledExpand,
  expand,
  showCancel,
  onCancel,
}: ThinkingProcessProps) => {
  const [open, setOpen] = useState(expand);
  // 整个组件的 ref
  const containerRef = useRef<HTMLDivElement>(null);
  // 内部滚动的 ref
  const contentRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef(0);

  const shouldActivateTyping = startTyping && (isThinking || open);

  // 打字机效果
  const { visibleList } = useTypewriter<ThinkingData>({
    list: thinkingList || [],
    speed: 25,
    enabled: shouldActivateTyping,
  });

  useEffect(() => {
    setOpen(expand);
  }, [expand]);

  // 当点击展开时，确保外部页面向下滚动，让内容可见
  useEffect(() => {
    if (open && !isThinking) {
      // 延迟一小会儿，等待 max-h-max 的 CSS 动画过渡开始
      setTimeout(() => {
        if (containerRef.current) {
          // 将组件顶部滚动到视口，考虑到可能存在 sticky header 的偏移
          containerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    }
  }, [open, isThinking]);

  // 组件内部滚动逻辑（仅在 180pt 模式下生效）
  useEffect(() => {
    const container = contentRef.current;
    if (shouldActivateTyping && container && !open) {
      const scrollHeight = container.scrollHeight;
      if (scrollHeight !== lastHeightRef.current) {
        container.scrollTo({
          top: scrollHeight,
          behavior: "auto",
        });
        lastHeightRef.current = scrollHeight;
      }
    }
  }, [visibleList, shouldActivateTyping, open]);

  const showContent = open || isThinking;

  return (
    // 整个组件绑定 containerRef
    <div ref={containerRef} className={cn("w-[575px] max-h-max pb-2", className)}>
      {/* 顶部标题：始终保持 Sticky */}
      <div className={cn("sticky top-0 z-20 bg-[rgba(249,250,251,1)] flex items-center")}>
        <div
          className={cn(
            "flex items-center gap-1 px-[10px] py-1 max-w-max transition-all",
            "hover:bg-[rgba(234,236,240,1)] rounded-[6px] cursor-pointer select-none",
            headerClassName
          )}
          onClick={() => {
            if (!enabledExpand) return;
            setOpen(!open);
          }}
        >
          {isThinking ? (
            <TextAnimateGradient text={title} className={cn("text-sm font-medium", titleClassName)} />
          ) : (
            <span className={cn("text-sm font-medium text-[rgba(102,112,133,1)]", titleClassName)}>{title}</span>
          )}
          {enabledExpand && (
            <ChevronRight
              className={cn(
                "size-4 text-[rgba(102,112,133,1)] transition-transform flex-shrink-0",
                titleClassName,
                open && "rotate-90"
              )}
            />
          )}
        </div>
        {showCancel && (
          <div onClick={onCancel}>
            <CancelTaskIcon className="text-[#eaecf0] hover:text-[#d0d5dd] transition-all active:scale-90 ml-3 cursor-pointer" />
          </div>
        )}
      </div>

      {/* 内容容器 */}
      {showContent && (
        <div
          className={cn(
            "relative mt-3 overflow-hidden transition-[height] duration-300",
            isThinking && !open && thinkingList.length > 0 ? "max-h-[180pt]" : (open ? "max-h-max" : "h-0")
          )}
        >
          {/* 蒙层 */}
          {isThinking && !open && visibleList.length > 0 && (
            <div className="absolute top-0 left-0 right-0 h-10 z-10 pointer-events-none bg-[linear-gradient(180deg,_rgba(249,_250,_251,_1)_0%,_rgba(249,_250,_251,_0)_100%)]" />
          )}

          {/* 滚动区域 */}
          <div
            ref={contentRef}
            className={cn(
              "w-full scrollbar-hide",
              // 关键：展开后去掉内部滚动，由外部页面接管
              isThinking && !open ? "h-full overflow-y-auto" : "h-auto overflow-y-visible"
            )}
          >
            <ThinkingTimeline list={startTyping ? visibleList : thinkingList} />
          </div>
        </div>
      )}
    </div>
  );
};

/** 思考过程时间轴 */
const ThinkingTimeline = ({ list }: { list: ThinkingData[] }) => {
  return (
    <div className="relative flex flex-col gap-3">
      {list.map((item) => (
        <ThinkingTimelineItem
          key={item.id}
          data={item}
        />
      ))}
    </div>
  )
}

/** 思考过程时间轴项 */
const ThinkingTimelineItem = ({
  data
}: { data: ThinkingData & { isComplete?: boolean } }) => {
  const characters = data.content.split("");

  return (
    <div className="relative flex gap-[17.5px]">
      {/* 左侧时间轴 */}
      <div className="relative flex flex-col items-center">
        <DotIcon className="mt-0.5" />
        <span className="flex-1 w-px bg-[rgba(208,213,221,1)]" />
      </div>

      {/* 右侧内容 */}
      <div className="flex flex-col gap-3 pb-2">
        <div className="text-sm font-medium text-[rgba(16,24,40,1)] -ml-1">
          {data.title}
        </div>
        <div className="text-sm leading-relaxed text-[rgba(102,112,133,1)] whitespace-pre-wrap">
          {data.isComplete ? data.content : (
            characters.map((char, index) => (
              <span key={index} className="animate-fade-in">{char}</span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}